import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Check, X, TrendingUp, TrendingDown, Equal, Sparkles } from "lucide-react";

function CorrosionBars({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`h-1.5 w-4 rounded-sm ${
            i <= rating ? "bg-nibro-sage-dark" : "bg-zinc-200"
          }`}
        />
      ))}
    </div>
  );
}

export default function MaterialComparison({ productId, currentMaterial, dnContext }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    api.get(`/products/${productId}/related-materials`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading || !data || data.siblings.length === 0) return null;

  const current = data.current;
  const seenMaterials = new Set([current.material]);
  const uniqueSiblings = (data.siblings || []).filter((s) => {
    if (seenMaterials.has(s.material)) return false;
    seenMaterials.add(s.material);
    return true;
  });
  const all = [
    { ...current, name: current.material_profile.name, isCurrent: true, image: null },
    ...uniqueSiblings.map((s) => ({ ...s, isCurrent: false })),
  ].sort((a, b) => a.material_profile.corrosion_rating - b.material_profile.corrosion_rating);

  return (
    <div className="mt-8 rounded-md border-2 border-nibro-sage bg-white" data-testid="material-comparison">
      <div className="flex items-center gap-2 border-b border-nibro-sage/30 bg-nibro-sage-pale px-5 py-3">
        <Sparkles size={16} className="text-nibro-sage-dark" />
        <h2 className="font-display text-base font-bold uppercase tracking-wide text-nibro-sage-dark">
          Vergelijk materialen
        </h2>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-zinc-500">
          {all.length} varianten beschikbaar
        </span>
      </div>

      <div className="grid grid-cols-1 divide-y divide-zinc-100 md:grid-cols-3 md:divide-x md:divide-y-0">
        {all.map((m) => {
          const prof = m.material_profile;
          const diff = m.price_from - current.price_from;
          const diffPct = current.price_from > 0 ? (diff / current.price_from) * 100 : 0;
          return (
            <div
              key={m.material}
              className={`flex flex-col p-5 ${m.isCurrent ? "bg-nibro-sage-pale" : ""}`}
              data-testid={`material-card-${m.material}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Materiaal
                  </div>
                  <div className="mt-1 font-display text-xl font-bold text-zinc-900">
                    {prof.name}
                  </div>
                </div>
                {m.isCurrent && (
                  <span className="rounded bg-nibro-sage px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white">
                    Huidig
                  </span>
                )}
              </div>

              <div className="mt-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                    Corrosie
                  </span>
                  <span className="text-xs font-semibold text-zinc-700">{prof.corrosion_label}</span>
                </div>
                <CorrosionBars rating={prof.corrosion_rating} />
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <Spec label="Max temp" value={`${prof.temp_max}°C`} />
                <Spec label="Vanaf prijs" value={`€${m.price_from.toFixed(2)}`} />
              </div>

              {!m.isCurrent && Math.abs(diffPct) > 1 && (
                <div
                  className={`mt-2 inline-flex items-center gap-1.5 self-start rounded px-2 py-1 font-mono text-[11px] font-bold ${
                    diff > 0 ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"
                  }`}
                >
                  {diff > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {diff > 0 ? "+" : ""}{diffPct.toFixed(0)}% vs huidig
                </div>
              )}

              <p className="mt-3 text-xs leading-relaxed text-zinc-600">{prof.summary}</p>

              {prof.recommended_for.length > 0 && (
                <div className="mt-3">
                  <div className="mb-1.5 flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    <Check size={11} /> Aanbevolen voor
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {prof.recommended_for.slice(0, 4).map((r) => (
                      <span key={r} className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-800">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {prof.not_recommended_for.length > 0 && (
                <div className="mt-3">
                  <div className="mb-1.5 flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-800">
                    <X size={11} /> Niet voor
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {prof.not_recommended_for.slice(0, 4).map((r) => (
                      <span key={r} className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {!m.isCurrent && (
                <Link
                  to={dnContext ? `/products/${m.id}?dn=${dnContext}` : `/products/${m.id}`}
                  className="mt-auto pt-4"
                  data-testid={`switch-to-${m.material}`}
                >
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-nibro-sage-dark hover:underline">
                    Bekijk {prof.name.split(" · ")[0]} <ArrowRight size={12} />
                  </span>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-400">{label}</div>
      <div className="mt-0.5 font-mono text-sm font-bold text-zinc-900">{value}</div>
    </div>
  );
}
